import { useState, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ColumnDef, FilterFn } from '@tanstack/react-table';

import useFundContext from '@/contexts/Fund/useFundContext';
import { ProjectDtoIn } from '@/models/in/ProjectDtoIn';
import { formatProjectAmount, getProjectStatusColor, getProjectStatusLabelKey } from '@/helpers/projectStatus';
import DataTable from '@/components/ui/DataTable/DataTable';
import { Col } from '@/components/ui/DataTable/ColumnsHelpers';
import { useGetAuthApi } from '@/hooks/UseApiCalls';
import Button from '@/components/ui/Button';
import SearchInput from '@/components/ui/SearchInput/SearchInput';
import NewProjectDialog from '@/pages/newProject/NewProjectDialog';
import ProjectBlade from './ProjectBlade/ProjectBlade';
import ApprovalStatusTabs from './ApprovalStatusTabs';
import { ApprovalTab, countByTab, projectApprovalTab } from './approvalTabs';

const projectSearch: FilterFn<ProjectDtoIn> = (row, _columnId, value) => {
  if (!value) return true;
  const searchValue = String(value).toLowerCase();
  const { name, projectTypeName, statusComment } = row.original;
  return [name, projectTypeName, statusComment].some((field) => field?.toLowerCase().includes(searchValue));
};

/**
 * The fund's projects list ("רשימת פרויקטים") — columns follow the designer's spec:
 * שם פרויקט | בקשות פתוחות | יתרת תקציב | סטטוס | הערות.
 *
 * "בקשות פתוחות" is not wired yet: linking support requests to a project is a separate
 * feature, so the column renders an em dash until that link exists rather than showing a
 * misleading zero.
 */
const ProjectsPage = () => {
  const { t, i18n } = useTranslation();
  const { currentFund } = useFundContext();
  const navigate = useNavigate();
  const { fundId: routeFundId } = useParams();

  const [searchQuery, setSearchQuery] = useState('');
  const [isNewProjectOpen, setIsNewProjectOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // HAT-1105: the support-request dialog sends a coordinator who has no approved project here with
  // ?new=1, so the wizard is already open when they arrive. The flag is removed at once: a reload or
  // a Back must not reopen it.
  useEffect(() => {
    if (searchParams.get('new') !== '1') return;
    setIsNewProjectOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('new');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);
  const [selectedProject, setSelectedProject] = useState<ProjectDtoIn | null>(null);

  const { data: projects, isLoading, refetch } = useGetAuthApi<ProjectDtoIn[]>(
    currentFund?.id ? `funds/${currentFund.id}/projects` : ''
  );

  const [tab, setTab] = useState<ApprovalTab>('all');
  const allProjects = useMemo(() => projects ?? [], [projects]);
  const tabCounts = useMemo(() => countByTab(allProjects, (project) => projectApprovalTab(project.status)), [allProjects]);
  const visibleProjects = useMemo(
    () => (tab === 'all' ? allProjects : allProjects.filter((project) => projectApprovalTab(project.status) === tab)),
    [allProjects, tab]
  );

  const handleCloseNewProject = useCallback(() => setIsNewProjectOpen(false), []);

  const columns: ColumnDef<ProjectDtoIn>[] = [
    {
      id: 'name',
      header: Col(t('projects.list.name')),
      accessorKey: 'name',
      enableSorting: true,
      meta: { className: 'min-w-[220px]' },
    },
    {
      id: 'openRequests',
      header: Col(t('projects.list.openRequests')),
      accessorKey: 'openRequestsCount',
      enableSorting: true,
      cell: ({ row }) => (
        <span>{t('projects.list.openRequestsCount', { count: row.original.openRequestsCount })}</span>
      ),
      meta: { className: 'min-w-[120px]' },
    },
    {
      id: 'budgetBalance',
      header: Col(t('projects.list.budgetBalance')),
      accessorKey: 'usedAmount',
      enableSorting: true,
      // used / total, as the design draws it. "used" counts only requests that reached Done -
      // an approved-but-unexecuted request is a commitment, not an expense.
      cell: ({ row }) => (
        <span className="text-(--font-tertiary)">
          {formatProjectAmount(row.original.usedAmount, i18n.language)}
          {' / '}
          {formatProjectAmount(row.original.totalExpensesAmount, i18n.language)}
        </span>
      ),
      meta: { className: 'min-w-[190px]' },
    },
    {
      id: 'status',
      header: Col(t('projects.list.status')),
      // Sortable like every other column here: arrows on only some headers read as arbitrary.
      accessorKey: 'status',
      enableSorting: true,
      cell: ({ row }) => (
        <div className="flex flex-col gap-0.5">
          <div className="flex items-center gap-2">
            <div
              className="w-[10px] h-[10px] rounded-full shrink-0"
              style={{ backgroundColor: getProjectStatusColor(row.original.status) }}
            />
            <span>{t(getProjectStatusLabelKey(row.original.status))}</span>
          </div>
          <span className="body-xs-regular text-(--font-secondary)">
            {Intl.DateTimeFormat('he-IL', { day: '2-digit', month: '2-digit', year: 'numeric' })
              .format(new Date(row.original.updatedAt ?? row.original.createdAt))}
          </span>
        </div>
      ),
      meta: { className: 'min-w-[170px]' },
    },
    {
      id: 'comments',
      header: Col(t('projects.list.comments')),
      accessorKey: 'statusComment',
      enableSorting: true,
      // The admin's rejection/approval comment is what a coordinator needs here; their own
      // notes are already visible in the project details. An em dash rather than an empty cell,
      // so a row without a comment does not look like missing data.
      cell: ({ row }) => (
        <span className={row.original.statusComment ? '' : 'text-(--font-tertiary)'}>
          {row.original.statusComment || '—'}
        </span>
      ),
      meta: { className: 'min-w-[180px]' },
    },
  ];

  return (
    <div>
      {/* The design's "חזרה למטרות תמיכה" link is gone with the screen's promotion to a section
          of its own in the nav (HAT-1105, office request 24/09): a top-level screen that opens
          with a link back to another screen still reads as that screen's sub-page, which is the
          impression the office asked us to remove. Arriving from the "הוצאות פרויקט" tile still
          works - מטרות תמיכה is one click away in the nav, as it is from every other section. */}
      <ApprovalStatusTabs value={tab} onChange={setTab} counts={tabCounts} />

      <DataTable
        title={t('projects.list.titleWithCount', { count: visibleProjects.length })}
        data={visibleProjects}
        isLoading={isLoading}
        columns={columns}
        filters={
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button variant="primary" onClick={() => setIsNewProjectOpen(true)}>
                {t('projects.list.add')}
              </Button>
              {/* The supplier form has no place in the design (suppliers appear nowhere in it);
                  the task asks for it, so it is reachable from here now that the sidebar
                  sub-tabs are gone. */}
              <Button
                variant="outline"
                onClick={() => navigate(`/${routeFundId}/projects/new-supplier`)}
              >
                {t('projects.sidebar.newSupplier')}
              </Button>
            </div>
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder="projects.list.search"
            />
          </div>
        }
        emptyStateContent={
          // A tab that is empty is not a fund without projects: say which, so "אין מיזמים מאושרים"
          // is not read as "you have no projects at all".
          tab === 'all' || (projects?.length ?? 0) === 0
            ? { title: t('projects.list.noData'), description: t('projects.list.noDataDescription') }
            : { title: t('projects.list.noDataInTab'), description: t('projects.list.noDataInTabDescription') }
        }
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        customGlobalSearch={projectSearch}
        handleRowClick={setSelectedProject}
      />

      <NewProjectDialog
        open={isNewProjectOpen}
        onClose={handleCloseNewProject}
        onSuccess={refetch}
      />

      {selectedProject && (
        <ProjectBlade
          project={selectedProject}
          onClose={() => setSelectedProject(null)}
          onAnswered={(updated) => {
            // Keep the blade open on the answered project, and refresh the list behind it.
            setSelectedProject(updated);
            refetch();
          }}
        />
      )}
    </div>
  );
};

export default ProjectsPage;
